/**
 * Problem Order Form — front-end controller.
 * 4-stage wizard (Details -> Products -> Issues -> Review). Collects a per-product
 * issue taxonomy via dropdowns (single-select for category/preference/thickness,
 * multi-select dropdown for products and Fit sub-reasons) and POSTs to the Salesforce
 * ProblemOrderIntakeAPI (/services/apexrest/order-issues/submit).
 *
 * Resume support: the form auto-saves a draft to localStorage on every change and
 * restores it on reload (everything except photos, which exceed the storage quota).
 */
(function () {
  'use strict';

  var MAX_IMAGE_DIM = 1600;
  var IMAGE_QUALITY = 0.8;
  var STORAGE_KEY = 'remiProblemOrderDraft';
  var saveTimer = null;

  // Values MUST match the Salesforce picklists
  // (Order_Issue_Item__c.Issue_Category__c and .Sub_Reasons__c).
  var ISSUE_CATEGORIES = [
    { value: 'Fit Issues', label: 'Fit Issues', multi: true, subReasons: [
      'Too tight or snug', 'Too loose', 'Too small', 'Too shallow or not covering enough teeth',
      'Too long or past gumline', 'Lopsided or uneven', 'Cutting into gums or pressure',
      'Molar issue', 'Extended past gumline', 'Other fit issue'
    ] },
    { value: 'Order Preference Issue', label: 'Order Preference Issue', multi: false, subReasons: [
      'Incorrect set preference', 'Other preference issue'
    ] },
    { value: 'Thickness Issue', label: 'Thickness Issue', multi: false, subReasons: [
      'Too thick', 'Too thin'
    ] },
    { value: 'Defective or Damaged Item', label: 'Defective or Damaged Item', multi: false, subReasons: [
      'Arrived damaged in transit', 'Does not work or faulty', 'Quality issue'
    ] },
    { value: 'Missing Incorrect or Incomplete Items', label: 'Missing, Incorrect or Incomplete Items', multi: false, subReasons: [
      'Missing or incomplete order', 'Incorrect item received'
    ] },
    { value: 'Shipping or Delivery Issue', label: 'Shipping or Delivery Issue', multi: false, subReasons: [
      'Lost in transit or never arrived', 'Marked delivered but not received',
      'Sent to wrong address', 'Tracking stuck', 'Other shipping issue'
    ] },
    { value: 'Others', label: 'Others', multi: false, subReasons: [] }
  ];

  // Photos required for every issue type except these.
  var ISSUE_PHOTO_OPTIONAL = ['Shipping or Delivery Issue', 'Others'];
  var PREFERENCE_OPTIONS = ['1 top and 1 bottom', '2 tops', '2 bottoms', '1 top', '1 bottom'];
  var THICKNESS_OPTIONS = ['1mm', '1.3mm', '2mm'];
  var FIT_PART_OPTIONS = ['Top', 'Bottom', 'Top and Bottom'];

  // Per sub-reason guidance shown under the "Photos" label: what the customer should send.
  var SUBREASON_GUIDE = {
    // Fit Issues
    'Too tight or snug': 'Put the guard on and take a photo while it is seated. If it is hard to get on or will not fully seat, send a short video of you trying to put it on so we can see where it catches.',
    'Too loose': 'Put the guard on, open your mouth, tap your teeth together, and gently try to dislodge it with your tongue. Send a short video so we can see if it slips or falls out. A photo cannot show looseness.',
    'Too small': 'Put the guard on and photograph it seated, showing the teeth it does not reach. If it will not go all the way on, add a short video of you trying to fit it.',
    'Too shallow or not covering enough teeth': 'Put it on and take a front photo showing where the edge stops on your teeth.',
    'Too long or past gumline': 'Put it on and photograph the spot where it presses into or covers your gums.',
    'Lopsided or uneven': 'Put it on and take a front-facing photo so we can see one side sitting differently from the other.',
    'Cutting into gums or pressure': 'Put it on and photograph the exact spot where it digs in. If there is redness or a sore spot, take a second photo of that area with the guard off.',
    'Molar issue': 'Please lay your guard on a flat surface with the molar side facing up, and take a clear photo. Let us know which molar is giving you trouble so we can pinpoint the issue.',
    'Extended past gumline': 'Put it on and photograph where it sits over your gumline.',
    'Other fit issue': 'Put it on and take a photo or short video showing the problem as clearly as you can.',
    // Order Preference Issue
    'Incorrect set preference': 'Take a photo of the guard(s) you received laid out, so we can see whether you got top, bottom, or the wrong set versus what you ordered.',
    'Other preference issue': 'Photograph what you received so we can compare it to your order.',
    // Thickness Issue
    'Too thick': 'Take a side-angle photo of the guard (off your teeth) so we can see how thick it is. If it is so thick you cannot bite or close normally, add a short video of you trying to bite down with it on.',
    'Too thin': 'Take a close side photo so we can see the thickness. If it is flimsy or bends easily, send a quick video of you gently flexing it.',
    // Defective or Damaged Item
    'Arrived damaged in transit': 'Take a close photo of the damaged part. If it arrived broken in the package, a photo of it in the packaging helps too.',
    'Does not work or faulty': 'Photograph the part that is not working. If it should move, light up, or run (like a device), send a short video of it failing to work.',
    'Quality issue': 'Take a close, well-lit photo of the exact spot where you see the problem.',
    // Missing, Incorrect or Incomplete Items
    'Missing or incomplete order': 'Lay out everything you received and take one photo of it all together, so we can see what arrived.',
    'Incorrect item received': 'Photograph the item you got next to the packing slip (if you have it) so we can compare it to your order.',
    // Shipping or Delivery Issue (no photo required)
    'Lost in transit or never arrived': 'No photo needed. A screenshot of your tracking is helpful if you have one.',
    'Marked delivered but not received': 'No photo needed. If the carrier sent a delivery photo or notification, that helps.',
    'Sent to wrong address': 'No photo needed. Just confirm your correct address in the details.',
    'Tracking stuck': 'No photo needed. A screenshot of the tracking page helps if you have one.',
    'Other shipping issue': 'No photo needed unless you have something that shows the problem.'
  };
  // Category-level guidance for categories with no sub-reasons.
  var CATEGORY_GUIDE = {
    'Others': 'No photo needed. Add anything that helps explain the issue.'
  };

  function categoryByValue(v) {
    for (var i = 0; i < ISSUE_CATEGORIES.length; i++) {
      if (ISSUE_CATEGORIES[i].value === v) return ISSUE_CATEGORIES[i];
    }
    return null;
  }
  function needsPreference(name) { return /custom night guard|custom removable retainer|custom whitening kit|emergency 3d night guard|bundle/i.test(name); }
  function isImpressionsKit(name) { return /impressions kit/i.test(name); }
  // Issue types not applicable to an Impressions Kit (they relate to the finished guards).
  var IMPRESSION_KIT_HIDDEN_CATEGORIES = ['Fit Issues', 'Order Preference Issue', 'Thickness Issue'];
  function el(tag, attrs, html) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    if (html != null) n.innerHTML = html;
    return n;
  }
  function fieldVal(form, name) { var i = form.querySelector('[name="' + name + '"]'); return i ? i.value.trim() : ''; }

  document.addEventListener('DOMContentLoaded', init);
  function init() {
    document.querySelectorAll('[data-problem-order-form-v2]').forEach(initForm);
  }

  function initForm(form) {
    var root = form.closest('.impression-form-v2') || form.parentNode;
    var lookupEndpoint = form.dataset.salesforceLookupEndpoint;
    var submitEndpoint = form.dataset.salesforceEndpoint;
    var welcomeModal = root.querySelector('[data-welcome-modal]');
    var thankYouModal = root.querySelector('[data-thank-you-modal]');

    var stages = form.querySelectorAll('[data-stage]');
    // The progress bar lives in the container, OUTSIDE the <form>, so query from root.
    var progressSteps = root.querySelectorAll('[data-progress-step]');
    var progressConnectors = root.querySelectorAll('[data-progress-connector]');

    var products = [];
    try { products = JSON.parse(form.dataset.products || '[]'); } catch (e) { products = []; }

    var state = {
      originalOrderId: null,
      issuesBuilt: false,
      productsMs: null,
      photosByProduct: {},   // per product: [{ name, type, viewUrl }]
      uploadingCount: 0,     // in-flight S3 uploads (blocks Next until done)
      mediaEndpoint: form.dataset.salesforceMediaEndpoint || '',
      currentStage: 1
    };
    form._poState = state;

    initTooltips(root);
    initStage1(form, lookupEndpoint, welcomeModal);

    var msContainer = form.querySelector('[data-products-multiselect]');
    state.productsMs = createMultiSelect(msContainer, products, 'Select affected product(s)…', function () {
      state.issuesBuilt = false;
      scheduleSave(form);
    }, true);

    // Auto-save the draft on any input/change
    form.addEventListener('input', function () { scheduleSave(form); });
    form.addEventListener('change', function () { scheduleSave(form); });

    form.querySelectorAll('[data-next-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var from = parseInt(btn.dataset.nextBtn, 10);
        if (!validateStage(from, form)) return;
        if (from === 2) buildIssueBlocks(form);
        if (from === 3) buildReview(form);
        goToStage(from + 1, form, stages, progressSteps, progressConnectors);
      });
    });
    form.querySelectorAll('[data-back-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var from = parseInt(btn.dataset.backBtn, 10);
        goToStage(from - 1, form, stages, progressSteps, progressConnectors);
      });
    });
    progressSteps.forEach(function (step) {
      step.addEventListener('click', function () {
        if (step.classList.contains('is-disabled')) return;
        var target = parseInt(step.dataset.progressStep, 10);
        if (target >= 3 && !validateStage(2, form)) return;
        if (target === 3) buildIssueBlocks(form);
        if (target === 4) { if (!validateStage(3, form)) return; buildReview(form); }
        goToStage(target, form, stages, progressSteps, progressConnectors);
      });
    });

    if (thankYouModal) {
      var closeBtn = thankYouModal.querySelector('[data-thank-you-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { window.location.reload(); });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submitForm(form, submitEndpoint, thankYouModal);
    });

    // Resume a saved draft, if any — ask the customer first
    var draft = loadDraft();
    if (draft) showResumePrompt(form, root, draft, stages, progressSteps, progressConnectors);
  }

  function showResumePrompt(form, root, draft, stages, steps, connectors) {
    var modal = el('div', { 'class': 'impression-form-v2__thank-you-modal is-open', 'data-resume-modal': '' });
    modal.innerHTML =
      '<div class="impression-form-v2__thank-you-backdrop"></div>' +
      '<div class="impression-form-v2__thank-you-content">' +
        '<h3 class="impression-form-v2__thank-you-title">Welcome back</h3>' +
        '<p class="impression-form-v2__thank-you-message">You have a problem order already in progress. Would you like to continue where you left off?</p>' +
        '<div class="impression-form-v2__welcome-buttons">' +
          '<button type="button" class="button button--outline" data-resume-fresh>Start fresh</button>' +
          '<button type="button" class="button" data-resume-continue>Continue</button>' +
        '</div>' +
      '</div>';
    root.appendChild(modal);
    modal.querySelector('[data-resume-continue]').addEventListener('click', function () {
      modal.remove();
      restoreDraft(form, draft, stages, steps, connectors);
    });
    modal.querySelector('[data-resume-fresh]').addEventListener('click', function () {
      clearDraft();
      modal.remove();
    });
  }

  /* ---------------- Draft persistence (localStorage) ---------------- */
  function loadDraft() {
    try { var s = localStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch (e) { return null; }
  }
  function clearDraft() { try { localStorage.removeItem(STORAGE_KEY); } catch (e) {} }
  function scheduleSave(form) {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(function () { saveDraft(form); }, 300);
  }
  function saveDraft(form) {
    try {
      var state = form._poState;
      var issues = {};
      form.querySelectorAll('[data-issue-block]').forEach(function (block) {
        var subs = [];
        if (block._subMs) subs = block._subMs.getValues();
        else { var ss = block.querySelector('[data-issue-subreason]'); if (ss && ss.value) subs = [ss.value]; }
        var variant = block.querySelector('[data-issue-variant]');
        issues[block.dataset.issueBlock] = {
          variant: variant ? variant.value : '',
          category: (block.querySelector('[data-issue-category]') || {}).value || '',
          subReasons: subs,
          fitPart: (block.querySelector('[data-issue-fitpart]') || {}).value || '',
          description: (block.querySelector('[data-issue-description]') || {}).value || '',
          preference: (block.querySelector('[data-issue-preference]') || {}).value || '',
          thickness: (block.querySelector('[data-issue-thickness]') || {}).value || ''
        };
      });
      var draft = {
        v: 1,
        stage: state.currentStage || 1,
        fields: {
          email: fieldVal(form, 'email'), firstName: fieldVal(form, 'firstName'), lastName: fieldVal(form, 'lastName'),
          phone: fieldVal(form, 'phone'), orderNumber: fieldVal(form, 'orderNumber'), orderDetails: fieldVal(form, 'orderDetails')
        },
        needMoreInfo: !!(form.querySelector('[data-need-more-info]') || {}).checked,
        originalOrderId: state.originalOrderId || null,
        products: getSelectedProducts(form),
        issues: issues
      };
      // Only persist once there's something worth restoring
      if (draft.fields.email || draft.fields.firstName || draft.products.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
      }
    } catch (e) {}
  }

  function restoreDraft(form, draft, stages, steps, connectors) {
    try {
      var f = draft.fields || {};
      Object.keys(f).forEach(function (k) {
        var input = form.querySelector('[name="' + k + '"]');
        if (input && f[k]) { input.value = f[k]; input.classList.add('is-floating'); }
      });
      var od = form.querySelector('[data-order-details-wrapper]');
      var on = form.querySelector('[name="orderNumber"]');
      if (od && on) od.style.display = on.value.trim() ? 'none' : 'block';
      var nmi = form.querySelector('[data-need-more-info]'); if (nmi) nmi.checked = !!draft.needMoreInfo;
      form._poState.originalOrderId = draft.originalOrderId || null;

      if (draft.products && draft.products.length && form._poState.productsMs) {
        form._poState.productsMs.setValues(draft.products);
      }

      var target = draft.stage || 1;
      if (target >= 3 && draft.products && draft.products.length) {
        buildIssueBlocks(form);
        form.querySelectorAll('[data-issue-block]').forEach(function (block) {
          var saved = (draft.issues || {})[block.dataset.issueBlock];
          if (!saved) return;
          var variant = block.querySelector('[data-issue-variant]'); if (variant && saved.variant) variant.value = saved.variant;
          var catSel = block.querySelector('[data-issue-category]');
          if (catSel && saved.category) {
            catSel.value = saved.category;
            catSel.dispatchEvent(new Event('change'));
            if (block._subMs && saved.subReasons) block._subMs.setValues(saved.subReasons);
            else { var ss = block.querySelector('[data-issue-subreason]'); if (ss && saved.subReasons && saved.subReasons[0]) ss.value = saved.subReasons[0]; }
            renderPhotoGuide(block);
          }
          var fitp = block.querySelector('[data-issue-fitpart]'); if (fitp) fitp.value = saved.fitPart || '';
          var desc = block.querySelector('[data-issue-description]'); if (desc) desc.value = saved.description || '';
          var pref = block.querySelector('[data-issue-preference]'); if (pref) pref.value = saved.preference || '';
          var thick = block.querySelector('[data-issue-thickness]'); if (thick) thick.value = saved.thickness || '';
        });
      }
      if (target >= 4) buildReview(form);

      injectResumeNote(form);
      goToStage(Math.min(target, 4), form, stages, steps, connectors);
    } catch (e) {}
  }

  function injectResumeNote(form) {
    if (form.querySelector('[data-resume-note]')) return;
    var note = el('div', { 'data-resume-note': '', 'style': 'font-size:0.85rem;margin-bottom:1rem;color:rgba(var(--text-color,0,0,0),0.7);display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;' });
    note.appendChild(el('span', null, '&#8617;&#65038; We restored your progress. Photos need to be re-added.'));
    var btn = el('button', { 'type': 'button', 'style': 'background:none;border:none;color:#0097BC;text-decoration:underline;cursor:pointer;padding:0;font-size:0.85rem;' }, 'Start over');
    btn.addEventListener('click', function () { clearDraft(); location.reload(); });
    note.appendChild(btn);
    form.insertBefore(note, form.firstChild);
  }

  /* ---------------- Multi-select dropdown widget ---------------- */
  function createMultiSelect(container, options, placeholder, onChange, searchable) {
    var selected = [];
    var cbByValue = {};
    var search = null;
    var searchRows = [];
    var wrap = el('div', { 'class': 'po-ms' });
    var toggle = el('div', { 'class': 'po-ms__toggle', 'tabindex': '0', 'role': 'button', 'aria-haspopup': 'listbox' });
    var panel = el('div', { 'class': 'po-ms__panel' });

    options.forEach(function (opt) {
      var row = el('label', { 'class': 'po-ms__option' });
      var cb = el('input', { 'type': 'checkbox' });
      cb.value = opt;
      cbByValue[opt] = cb;
      cb.addEventListener('change', function () {
        if (cb.checked) { if (selected.indexOf(opt) === -1) selected.push(opt); }
        else { selected = selected.filter(function (x) { return x !== opt; }); }
        renderToggle();
        if (onChange) onChange(selected.slice());
      });
      row.appendChild(cb);
      row.appendChild(el('span', null, escapeHtml(opt)));
      panel.appendChild(row);
      searchRows.push({ row: row, text: opt.toLowerCase() });
    });

    if (searchable) {
      var searchWrap = el('div', { 'class': 'po-ms__search' });
      search = el('input', { 'type': 'text', 'class': 'po-ms__search-input', 'placeholder': 'Search products…', 'aria-label': 'Search products' });
      searchWrap.appendChild(search);
      panel.insertBefore(searchWrap, panel.firstChild);
      var noMatch = el('div', { 'class': 'po-ms__no-match', 'style': 'display:none;padding:0.55rem 0.6rem;opacity:0.6;' }, 'No matches');
      panel.appendChild(noMatch);
      search.addEventListener('click', function (e) { e.stopPropagation(); });
      search.addEventListener('keydown', function (e) { e.stopPropagation(); });
      search.addEventListener('input', function () {
        var q = search.value.trim().toLowerCase();
        var any = false;
        searchRows.forEach(function (sr) {
          var show = !q || sr.text.indexOf(q) !== -1;
          sr.row.style.display = show ? '' : 'none';
          if (show) any = true;
        });
        noMatch.style.display = any ? 'none' : 'block';
      });
    }

    function removeValue(opt) {
      selected = selected.filter(function (x) { return x !== opt; });
      if (cbByValue[opt]) cbByValue[opt].checked = false;
      renderToggle();
      if (onChange) onChange(selected.slice());
    }
    function setValues(vals) {
      (vals || []).forEach(function (v) {
        var cb = cbByValue[v];
        if (cb && !cb.checked) { cb.checked = true; if (selected.indexOf(v) === -1) selected.push(v); }
      });
      renderToggle();
      if (onChange) onChange(selected.slice());
    }

    function renderToggle() {
      toggle.innerHTML = '';
      if (!selected.length) {
        toggle.appendChild(el('span', { 'class': 'po-ms__placeholder' }, escapeHtml(placeholder)));
        return;
      }
      selected.forEach(function (opt) {
        var chip = el('span', { 'class': 'po-ms__chip' });
        chip.appendChild(document.createTextNode(opt));
        var x = el('button', { 'type': 'button', 'class': 'po-ms__chip-x', 'aria-label': 'Remove' }, '&times;');
        x.addEventListener('click', function (e) { e.stopPropagation(); removeValue(opt); });
        chip.appendChild(x);
        toggle.appendChild(chip);
      });
    }

    toggle.addEventListener('click', function (e) {
      e.stopPropagation();
      document.querySelectorAll('.po-ms.is-open').forEach(function (o) { if (o !== wrap) o.classList.remove('is-open'); });
      wrap.classList.toggle('is-open');
      if (search && wrap.classList.contains('is-open')) { setTimeout(function () { search.focus(); }, 0); }
    });
    panel.addEventListener('click', function (e) { e.stopPropagation(); });
    document.addEventListener('click', function () { wrap.classList.remove('is-open'); });

    renderToggle();
    wrap.appendChild(toggle);
    wrap.appendChild(panel);
    if (container) container.appendChild(wrap);
    return { getValues: function () { return selected.slice(); }, setValues: setValues };
  }

  function nativeSelectHtml(attr, options, placeholder) {
    var s = '<select class="select select--native po-select" ' + attr + '><option value="">' + escapeHtml(placeholder || '—') + '</option>';
    options.forEach(function (o) { s += '<option value="' + escapeAttr(o) + '">' + escapeHtml(o) + '</option>'; });
    return s + '</select>';
  }
  // Issue-type select: submits the picklist value (c.value) but displays the friendlier c.label.
  function categorySelectHtml(attr, placeholder, product) {
    var hide = isImpressionsKit(product) ? IMPRESSION_KIT_HIDDEN_CATEGORIES : null;
    var s = '<select class="select select--native po-select" ' + attr + '><option value="">' + escapeHtml(placeholder || '—') + '</option>';
    ISSUE_CATEGORIES.forEach(function (c) {
      if (hide && hide.indexOf(c.value) !== -1) return;
      s += '<option value="' + escapeAttr(c.value) + '">' + escapeHtml(c.label) + '</option>';
    });
    return s + '</select>';
  }
  function categoryLabel(v) { var c = categoryByValue(v); return c ? c.label : v; }

  /* ---------------- Stage 1: details + lookup ---------------- */
  function enforceDigits(input, max) {
    if (!input) return;
    input.setAttribute('inputmode', 'numeric');
    input.addEventListener('input', function () {
      var v = input.value.replace(/\D/g, '');
      if (max) v = v.slice(0, max);
      if (v !== input.value) input.value = v;
    });
  }

  function formatUsPhone(input) {
    if (!input) return;
    input.setAttribute('inputmode', 'numeric');
    var fmt = function () {
      var d = input.value.replace(/\D/g, '').slice(0, 10);
      var out = d;
      if (d.length > 6) out = '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
      else if (d.length > 3) out = '(' + d.slice(0, 3) + ') ' + d.slice(3);
      input.value = out;
    };
    input.addEventListener('input', fmt);
    fmt();
  }

  function initStage1(form, lookupEndpoint, welcomeModal) {
    var email = form.querySelector('[name="email"]');
    var orderNumber = form.querySelector('[name="orderNumber"]');
    var phone = form.querySelector('[name="phone"]');
    var orderDetailsWrap = form.querySelector('[data-order-details-wrapper]');

    enforceDigits(orderNumber);
    formatUsPhone(phone);

    if (orderNumber && orderDetailsWrap) {
      var toggle = function () { orderDetailsWrap.style.display = orderNumber.value.trim() ? 'none' : 'block'; };
      orderNumber.addEventListener('input', toggle);
      toggle();
    }
    if (email && lookupEndpoint && welcomeModal) {
      email.addEventListener('blur', function () {
        var v = email.value.trim();
        if (!v || v.indexOf('@') === -1) return;
        lookupCustomer(v, lookupEndpoint, welcomeModal, form);
      });
    }
  }

  function lookupCustomer(emailValue, endpoint, welcomeModal, form) {
    fetch(endpoint + '?email=' + encodeURIComponent(emailValue) + '&all=true', { method: 'GET', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || data.success === false) return;
        prefillCustomer(form, data);
        var orders = data.orders || data.Orders || [];
        // Only show orders that have an order number.
        orders = orders.filter(function (o) { return (o.orderNumber || o.orderNo || o.name); });
        if (orders.length) showOrderModal(welcomeModal, orders, form);
      })
      .catch(function () {});
  }

  function prefillCustomer(form, data) {
    var map = { firstName: data.firstName, lastName: data.lastName, phone: data.phone };
    Object.keys(map).forEach(function (k) {
      var input = form.querySelector('[name="' + k + '"]');
      if (input && !input.value && map[k]) { input.value = map[k]; input.classList.add('is-floating'); }
    });
    scheduleSave(form);
  }

  function formatOrderDate(s) {
    if (!s) return '';
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (!m) return s;
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[parseInt(m[2], 10) - 1] + ' ' + parseInt(m[3], 10) + ', ' + m[1];
  }

  // SKU prefix -> customer-friendly product name. Salesforce Product_Name__c is
  // unreliable (often a record Id), so we resolve display names from the SKU.
  var SKU_NAME_MAP = [
    { re: /^R-NG-01/i,   name: 'Custom Night Guard' },
    { re: /^R-NG-02/i,   name: 'Custom Night Guard' },
    { re: /^R-CR-RET/i,  name: 'Custom Removable Retainer' },
    { re: /^R-NG-03/i,   name: 'Teeth Whitening Gel' },
    { re: /^R-NGUC/i,    name: 'Ultrasonic Cleaning & Sanitizing Device Pro' },
    { re: /^R-UVTS/i,    name: 'UV Toothbrush Sanitizer' },
    { re: /^R-RT-/i,     name: 'Night Guard & Retainer Removal Tool' },
    { re: /^R-WF-/i,     name: 'Night Guard Cleaning + Teeth Whitening Foam' },
    { re: /^R-C-ST-B/i,  name: 'The Starter Bundle' },
    { re: /^R-C-ESS-B/i, name: 'The Essentials Bundle' },
    { re: /^R-C-SUP-B/i, name: 'The Ultimate Bundle' },
    { re: /^PACKAGE PROTECTION/i, name: 'Package Protection' },
    { re: /^RUSH/i, name: 'Rush Lab Service' }
  ];
  function skuToProductName(sku) {
    if (!sku) return null;
    for (var i = 0; i < SKU_NAME_MAP.length; i++) { if (SKU_NAME_MAP[i].re.test(sku)) return SKU_NAME_MAP[i].name; }
    return null;
  }
  function looksLikeSfId(s) { return /^[a-zA-Z0-9]{15}$|^[a-zA-Z0-9]{18}$/.test(s); }
  // Strip promo wording (Discount / Sales Discount / % off / Bonus / Gift) from a product name.
  function cleanProductName(s) {
    if (!s) return s;
    var out = s.replace(/^\s*(bonus|gift)\s*[:\-–—]\s*/i, '');                          // leading "BONUS:" / "GIFT:"
    out = out.replace(/\s*[-–—(]?\s*(sales\s+discount|discount|bonus|gift)\s*\)?\s*$/i, ''); // trailing promo word
    out = out.replace(/\s*\d+(\.\d+)?\s*%\s*(off)?\s*$/i, '');                          // trailing "10% off"
    out = out.replace(/\s*%\s*$/i, '');                                                 // stray trailing %
    return out.trim();
  }

  function showOrderModal(modal, orders, form) {
    var list = modal.querySelector('[data-order-list]');
    var titleEl = modal.querySelector('.impression-form-v2__welcome-title');
    var subEl = modal.querySelector('.impression-form-v2__welcome-subtitle');
    var many = orders.length > 1;
    if (titleEl) titleEl.textContent = many ? 'We found your orders' : 'We found your order';
    if (subEl) subEl.textContent = many ? 'Which one has the issue? Select it below.' : 'Is this the order with the issue?';

    list.innerHTML = '';
    orders.forEach(function (o, i) {
      var id = o.orderId || o.id || o.Id || '';
      var num = o.orderNumber || o.orderNo || o.name || '';
      var title = num ? ('Order #' + num) : ('Order ' + (i + 1));
      var metaParts = [];
      var d = formatOrderDate(o.orderDate || o.createdDate);
      if (d) metaParts.push('Order Date: ' + d);
      var meta = metaParts.join(' · ');
      // Customer-facing product names, resolved from SKU. Hide discounts / rush / gifts /
      // record-Ids / unknowns so customers only ever see real, readable products.
      var displayItems = [];
      var seenNames = {};
      (o.items || []).forEach(function (it) {
        var sku = (it.sku || '').trim();
        if (sku && /^null/i.test(sku)) return;                     // junk SKU
        var nm = skuToProductName(sku);                            // SKU map (discount variants resolve too)
        if (!nm) {
          var pn = (it.productName || '').trim();
          if (pn && !looksLikeSfId(pn)) nm = pn;                   // clean name, but never a record Id
        }
        nm = cleanProductName(nm);                                 // strip "Discount" / "% off" suffix
        if (!nm) return;                                           // nothing usable -> skip
        if (seenNames[nm]) return;                                 // same product name -> show once
        seenNames[nm] = true;
        displayItems.push(nm);
      });
      var itemsHtml = displayItems.length
        ? '<ul class="impression-form-v2__order-items" style="margin:4px 0 0;padding-left:18px;font-size:0.9rem;">' +
            displayItems.map(function (s) { return '<li>' + escapeHtml(s) + '</li>'; }).join('') + '</ul>'
        : '';
      var opt = el('label', { 'class': 'impression-form-v2__order-option' });
      opt.innerHTML =
        '<input type="radio" name="po-order" class="impression-form-v2__order-radio" value="' + encodeURIComponent(id) + '" data-num="' + String(num).replace(/"/g, '') + '">' +
        '<div class="impression-form-v2__order-info">' +
          '<div class="impression-form-v2__order-title">' + escapeHtml(title) + '</div>' +
          (meta ? '<div class="impression-form-v2__order-meta">' + escapeHtml(meta) + '</div>' : '') +
          itemsHtml +
        '</div>';
      list.appendChild(opt);
    });
    modal.classList.add('is-open');
    var selectBtn = modal.querySelector('[data-welcome-select]');
    var skipBtn = modal.querySelector('[data-welcome-new]');
    var closeEls = modal.querySelectorAll('[data-welcome-close]');
    var close = function () { modal.classList.remove('is-open'); };
    if (selectBtn) selectBtn.onclick = function () {
      var checked = modal.querySelector('input[name="po-order"]:checked');
      if (checked) {
        form._poState.originalOrderId = decodeURIComponent(checked.value) || null;
        var numInput = form.querySelector('[name="orderNumber"]');
        if (numInput && checked.dataset.num) { numInput.value = checked.dataset.num; numInput.dispatchEvent(new Event('input')); }
        var odWrap = form.querySelector('[data-order-details-wrapper]');
        if (odWrap) odWrap.style.display = 'none';
        scheduleSave(form);
      }
      close();
    };
    if (skipBtn) skipBtn.onclick = close;
    closeEls.forEach(function (c) { c.onclick = close; });
  }

  /* ---------------- Stage 2 ---------------- */
  function getSelectedProducts(form) {
    return form._poState.productsMs ? form._poState.productsMs.getValues() : [];
  }

  /* ---------------- Stage 3: per-product issue blocks ---------------- */
  function buildIssueBlocks(form) {
    if (form._poState.issuesBuilt) return;
    var products = getSelectedProducts(form);
    var root = form.querySelector('[data-issues-root]');
    root.innerHTML = '';
    form._poState.photosByProduct = {};

    products.forEach(function (product) {
      var block = el('div', { 'class': 'impression-form-v2__conditional-options is-visible', 'data-issue-block': product });
      block.style.marginBottom = '1.5rem';

      var html = '<h4 class="impression-form-v2__stage-title" style="font-size:1.05rem;">' + escapeHtml(product) + '</h4>';

      html += '<div class="po-field"><label class="impression-form-v2__field-label">Issue type <span class="required">*</span></label>' +
        categorySelectHtml('data-issue-category', 'Select an issue type', product) + '</div>';

      html += '<div class="po-field" data-subreasons-wrap style="display:none;"></div>';

      html += '<div class="po-field" data-fitpart-wrap style="display:none;">' +
        '<label class="impression-form-v2__field-label">Which part has the issue? <span class="required">*</span></label>' +
        nativeSelectHtml('data-issue-fitpart', FIT_PART_OPTIONS, 'Select a part') + '</div>';

      html += '<div class="po-field"><label class="impression-form-v2__field-label">Details <span class="required">*</span></label>' +
        '<textarea class="textarea" rows="3" data-issue-description placeholder="Describe the issue"></textarea></div>';

      if (needsPreference(product)) {
        html += '<div class="input-row">' +
          '<div class="po-field"><label class="impression-form-v2__field-label">Preference</label>' + nativeSelectHtml('data-issue-preference', PREFERENCE_OPTIONS, '—') + '</div>' +
          '<div class="po-field"><label class="impression-form-v2__field-label">Thickness</label>' + nativeSelectHtml('data-issue-thickness', THICKNESS_OPTIONS, '—') + '</div>' +
          '</div>';
      }

      html += '<div data-photo-wrap class="po-field" style="display:none;">' +
        '<label class="impression-form-v2__upload-box-label" data-photo-label>Photos</label>' +
        '<div class="po-photo-guide" data-photo-guide style="display:none;"></div>' +
        '<div class="impression-form-v2__upload-area" data-upload-area>' +
        '<p class="impression-form-v2__upload-text"><strong>Tap or click to upload photos or video</strong><br>' +
        '<span style="font-size:0.9rem;">JPG, PNG, HEIC, or MP4/MOV · up to 100MB each</span></p></div>' +
        '<input type="file" class="impression-form-v2__file-input" data-file-input accept="image/*,video/*" multiple>' +
        '<div class="impression-form-v2__preview-grid" data-preview-grid></div></div>';

      html += '<p class="impression-form-v2__field-error" data-issue-error style="display:none;"></p>';

      block.innerHTML = html;
      root.appendChild(block);
      wireIssueBlock(form, block, product);
    });

    form._poState.issuesBuilt = true;
  }

  // Show "what to send" guidance under the Photos label for the selected sub-reason(s).
  function renderPhotoGuide(block) {
    var guide = block.querySelector('[data-photo-guide]');
    if (!guide) return;
    var catSel = block.querySelector('[data-issue-category]');
    var cat = catSel ? categoryByValue(catSel.value) : null;
    var subs = [];
    if (block._subMs) subs = block._subMs.getValues();
    else { var ss = block.querySelector('[data-issue-subreason]'); if (ss && ss.value) subs = [ss.value]; }

    var html = '';
    if (cat && !cat.subReasons.length && CATEGORY_GUIDE[cat.value]) {
      html = '<p class="po-photo-guide__text">' + escapeHtml(CATEGORY_GUIDE[cat.value]) + '</p>';
    } else if (subs.length) {
      subs.forEach(function (s) {
        var text = SUBREASON_GUIDE[s];
        if (!text) return;
        if (subs.length > 1) {
          html += '<p class="po-photo-guide__text"><span class="po-photo-guide__sub">' + escapeHtml(s) + ':</span> ' + escapeHtml(text) + '</p>';
        } else {
          html += '<p class="po-photo-guide__text">' + escapeHtml(text) + '</p>';
        }
      });
    }
    guide.innerHTML = html;
    guide.style.display = html ? 'block' : 'none';
  }

  function wireIssueBlock(form, block, product) {
    var catSelect = block.querySelector('[data-issue-category]');
    var subWrap = block.querySelector('[data-subreasons-wrap]');
    var fitWrap = block.querySelector('[data-fitpart-wrap]');
    var photoWrap = block.querySelector('[data-photo-wrap]');
    var photoLabel = block.querySelector('[data-photo-label]');

    catSelect.addEventListener('change', function () {
      var cat = categoryByValue(catSelect.value);
      if (fitWrap) {
        var isFit = cat && cat.value === 'Fit Issues';
        fitWrap.style.display = isFit ? 'block' : 'none';
        if (!isFit) { var fp = fitWrap.querySelector('[data-issue-fitpart]'); if (fp) fp.value = ''; }
      }
      block._subMs = null;
      subWrap.innerHTML = '';
      subWrap.style.display = 'none';
      if (cat && cat.subReasons.length) {
        subWrap.style.display = 'block';
        if (cat.multi) {
          subWrap.innerHTML = '<label class="impression-form-v2__field-label">Select all that apply <span class="required">*</span></label>';
          block._subMs = createMultiSelect(subWrap, cat.subReasons, 'Select sub-reason(s)…', function () { scheduleSave(form); renderPhotoGuide(block); });
        } else {
          subWrap.innerHTML = '<label class="impression-form-v2__field-label">Sub-reason <span class="required">*</span></label>' +
            nativeSelectHtml('data-issue-subreason', cat.subReasons, 'Select a sub-reason');
          var single = subWrap.querySelector('[data-issue-subreason]');
          if (single) single.addEventListener('change', function () { scheduleSave(form); renderPhotoGuide(block); });
        }
      }
      var requires = cat ? ISSUE_PHOTO_OPTIONAL.indexOf(cat.value) === -1 : false;
      photoWrap.style.display = cat ? 'block' : 'none';
      photoLabel.innerHTML = requires ? 'Photos <span class="required">*</span>' : 'Photos (optional)';
      block.dataset.requiresPhoto = requires ? '1' : '0';
      renderPhotoGuide(block);
    });

    var area = block.querySelector('[data-upload-area]');
    var input = block.querySelector('[data-file-input]');
    var grid = block.querySelector('[data-preview-grid]');
    form._poState.photosByProduct[product] = [];
    if (area && input) {
      area.addEventListener('click', function () { input.click(); });
      input.addEventListener('change', function () { handleFiles(form, input.files, form._poState.photosByProduct[product], grid, area); input.value = ''; });
    }
  }

  var MAX_UPLOAD_BYTES = 100 * 1024 * 1024; // 100 MB

  function handleFiles(form, fileList, store, grid, area) {
    Array.prototype.forEach.call(fileList, function (file) {
      var isImage = /^image\//.test(file.type) || /\.(jpe?g|png|heic|heif)$/i.test(file.name);
      var isVideo = /^video\//.test(file.type) || /\.(mp4|mov|m4v|webm|quicktime)$/i.test(file.name);
      if (!isImage && !isVideo) return;
      if (file.size > MAX_UPLOAD_BYTES) {
        alert('"' + file.name + '" is larger than 100MB. Please upload a smaller file.');
        return;
      }
      uploadToS3(form, file, isVideo ? 'video' : 'image', store, grid, area);
    });
  }

  // Request a presigned URL from Salesforce, PUT the file to S3, store the view link.
  function uploadToS3(form, file, kind, store, grid, area) {
    var endpoint = form._poState.mediaEndpoint;
    if (!endpoint) { alert('Uploads are temporarily unavailable.'); return; }

    var tile = el('div', { 'class': 'impression-form-v2__preview-item' });
    tile.style.position = 'relative';
    tile.innerHTML =
      '<div class="po-upload-progress" style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;padding:8px;box-sizing:border-box;">' +
        '<div style="font-size:0.65rem;color:rgba(0,0,0,0.6);margin-bottom:6px;">Uploading…</div>' +
        '<div style="width:100%;height:6px;border-radius:3px;background:rgba(0,151,188,0.15);overflow:hidden;">' +
          '<div data-progress-bar style="width:0%;height:100%;background:rgb(0,151,188);transition:width .15s ease;"></div>' +
        '</div>' +
        '<div data-progress-pct style="font-size:0.6rem;color:rgba(0,0,0,0.5);margin-top:4px;">0%</div>' +
      '</div>';
    grid.appendChild(tile);
    area.classList.add('has-files');
    form._poState.uploadingCount++;

    var bar = tile.querySelector('[data-progress-bar]');
    var pct = tile.querySelector('[data-progress-pct]');
    var ct = file.type || (kind === 'video' ? 'video/mp4' : 'image/jpeg');

    fetch(endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: ct })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data || !data.uploadUrl) throw new Error('no upload url');
        return putWithProgress(data.uploadUrl, file, ct, function (p) {
          if (bar) bar.style.width = p + '%';
          if (pct) pct.textContent = p + '%';
        }).then(function () {
          var record = { name: file.name, type: kind, viewUrl: data.viewUrl };
          store.push(record);
          if (kind === 'image') {
            var url = URL.createObjectURL(file);
            tile.innerHTML = '<img src="' + url + '" alt="preview">';
          } else {
            tile.innerHTML = '<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;font-size:0.7rem;text-align:center;padding:4px;">🎬<br>' + escapeHtml(file.name) + '</div>';
          }
          addRemoveButton(tile, store, record, grid, area);
        });
      })
      .catch(function () {
        tile.innerHTML = '<div class="po-upload-status" style="display:flex;align-items:center;justify-content:center;height:100%;font-size:0.7rem;color:#b00;text-align:center;padding:4px;cursor:pointer;">Upload failed — tap to retry</div>';
        tile.onclick = function () { tile.remove(); uploadToS3(form, file, kind, store, grid, area); };
      })
      .then(function () { form._poState.uploadingCount = Math.max(0, form._poState.uploadingCount - 1); });
  }

  // PUT to S3 via XHR so we can report real upload progress (fetch can't).
  function putWithProgress(url, file, contentType, onProgress) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.upload.onprogress = function (e) {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) { if (onProgress) onProgress(100); resolve(); }
        else reject(new Error('s3 put failed ' + xhr.status));
      };
      xhr.onerror = function () { reject(new Error('s3 put network error')); };
      xhr.send(file);
    });
  }

  // Adds an "×" button that removes an uploaded item from the preview + stored list.
  function addRemoveButton(tile, store, record, grid, area) {
    var btn = el('button', {
      'type': 'button',
      'class': 'po-remove-media',
      'aria-label': 'Remove',
      'style': 'position:absolute;top:2px;right:2px;width:20px;height:20px;border-radius:50%;border:none;'
        + 'background:rgba(0,0,0,0.65);color:#fff;font-size:13px;line-height:1;cursor:pointer;'
        + 'display:flex;align-items:center;justify-content:center;padding:0;z-index:2;'
    }, '×');
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      var i = store.indexOf(record);
      if (i !== -1) store.splice(i, 1);
      tile.remove();
      if (!grid.querySelector('.impression-form-v2__preview-item')) area.classList.remove('has-files');
    });
    tile.appendChild(btn);
  }

  /* ---------------- Stage 4: review ---------------- */
  function buildReview(form) {
    var summary = form.querySelector('[data-review-summary]');
    var data = collect(form);
    var html = '<div class="prose"><p><strong>Full Name:</strong> ' + escapeHtml((data.firstName || '') + ' ' + (data.lastName || '')) +
      '<br><strong>Email:</strong> ' + escapeHtml(data.email || '') +
      (data.phone ? '<br><strong>Phone:</strong> ' + escapeHtml(data.phone) : '') +
      (data.orderNumber ? '<br><strong>Order #:</strong> ' + escapeHtml(data.orderNumber) : '') + '</p>';
    data.products.forEach(function (p) {
      html += '<hr class="impression-form-v2__divider"><p><strong>' + escapeHtml(p.productName) + '</strong>' +
        (p.variant ? ' — ' + escapeHtml(p.variant) : '') +
        '<br><strong>Issue:</strong> ' + escapeHtml(p.issueCategory ? categoryLabel(p.issueCategory) : '—') +
        (p.subReasons && p.subReasons.length ? '<br><strong>Details:</strong> ' + escapeHtml(p.subReasons.join(', ')) : '') +
        (p.fitPart ? '<br><strong>Which part:</strong> ' + escapeHtml(p.fitPart) : '') +
        (p.description ? '<br><strong>Notes:</strong> ' + escapeHtml(p.description) : '') +
        (p.preference ? '<br><strong>Preference:</strong> ' + escapeHtml(p.preference) : '') +
        (p.thickness ? '<br><strong>Thickness:</strong> ' + escapeHtml(p.thickness) : '') +
        '<br><strong>Attachments:</strong> ' + (p.mediaUrls ? p.mediaUrls.length : 0) + '</p>';
    });
    summary.innerHTML = html + '</div>';
  }

  /* ---------------- Collect + submit ---------------- */
  function collect(form) {
    var state = form._poState;
    var products = [];
    form.querySelectorAll('[data-issue-block]').forEach(function (block) {
      var name = block.dataset.issueBlock;
      var cat = (block.querySelector('[data-issue-category]') || {}).value || '';
      var subs = [];
      if (block._subMs) subs = block._subMs.getValues();
      else { var ss = block.querySelector('[data-issue-subreason]'); if (ss && ss.value) subs = [ss.value]; }
      var variant = block.querySelector('[data-issue-variant]');
      products.push({
        productName: name,
        variant: variant ? variant.value.trim() : '',
        issueCategory: cat,
        subReasons: subs,
        fitPart: (block.querySelector('[data-issue-fitpart]') || {}).value || '',
        description: (block.querySelector('[data-issue-description]') || {}).value || '',
        preference: (block.querySelector('[data-issue-preference]') || {}).value || '',
        thickness: (block.querySelector('[data-issue-thickness]') || {}).value || '',
        requiresAttachment: block.dataset.requiresPhoto === '1',
        mediaUrls: (state.photosByProduct[name] || []).map(function (m) { return m.viewUrl; }).filter(Boolean)
      });
    });
    return {
      firstName: fieldVal(form, 'firstName'), lastName: fieldVal(form, 'lastName'), email: fieldVal(form, 'email'), phone: fieldVal(form, 'phone'),
      orderNumber: fieldVal(form, 'orderNumber'), orderDetails: fieldVal(form, 'orderDetails'),
      originalOrderId: state.originalOrderId,
      needMoreInfo: !!(form.querySelector('[data-need-more-info]') || {}).checked,
      products: products
    };
  }

  function submitForm(form, endpoint, thankYouModal) {
    if (!validateStage(1, form) || !validateStage(2, form) || !validateStage(3, form)) return;
    var payload = collect(form);
    setLoading(form, true);
    fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (res) {
        setLoading(form, false);
        if (res.body && res.body.success) { clearDraft(); showThankYou(thankYouModal, res.body.referenceNumber); }
        else showError(form, (res.body && res.body.message) || 'Submission failed. Please try again.');
      })
      .catch(function () { setLoading(form, false); showError(form, 'Network error. Please try again.'); });
  }

  /* ---------------- Validation ---------------- */
  function validateStage(n, form) {
    clearError(form);
    if (n === 1) {
      var req = ['firstName', 'lastName', 'email', 'phone'];
      for (var i = 0; i < req.length; i++) {
        var input = form.querySelector('[name="' + req[i] + '"]');
        if (!input || !input.value.trim()) { showError(form, 'Please complete all required fields.'); return false; }
      }
      if (form.querySelector('[name="email"]').value.trim().indexOf('@') === -1) { showError(form, 'Please enter a valid email.'); return false; }
      var phoneEl = form.querySelector('[name="phone"]');
      if (phoneEl && phoneEl.value.replace(/\D/g, '').length !== 10) { showError(form, 'Please enter a valid 10-digit US phone number.'); return false; }
      // No order number (no order found/selected) -> "Details of your order" is required.
      var orderNumEl = form.querySelector('[name="orderNumber"]');
      var orderDetEl = form.querySelector('[name="orderDetails"]');
      if (orderNumEl && !orderNumEl.value.trim() && (!orderDetEl || !orderDetEl.value.trim())) {
        showError(form, 'Please enter your order number, or describe your order in "Details of your order".');
        return false;
      }
      return true;
    }
    if (n === 2) {
      if (getSelectedProducts(form).length === 0) {
        var pe = form.querySelector('[data-products-error]'); if (pe) pe.style.display = 'block';
        showError(form, 'Select at least one affected product.');
        return false;
      }
      var pe2 = form.querySelector('[data-products-error]'); if (pe2) pe2.style.display = 'none';
      return true;
    }
    if (n === 3) {
      if (form._poState.uploadingCount > 0) {
        showError(form, 'Please wait for your photos/video to finish uploading.');
        return false;
      }
      var ok = true, firstBad = null;
      form.querySelectorAll('[data-issue-block]').forEach(function (block) {
        var err = block.querySelector('[data-issue-error]');
        if (err) err.style.display = 'none';
        var cat = (block.querySelector('[data-issue-category]') || {}).value;
        if (!cat) { if (err) { err.textContent = 'Choose an issue type.'; err.style.display = 'block'; } ok = false; firstBad = firstBad || block; return; }
        var catObj = categoryByValue(cat);
        if (catObj && catObj.subReasons.length) {
          var subs = block._subMs ? block._subMs.getValues() : [];
          if (!block._subMs) { var ss = block.querySelector('[data-issue-subreason]'); if (ss && ss.value) subs = [ss.value]; }
          if (!subs.length) { if (err) { err.textContent = 'Please select a sub-reason.'; err.style.display = 'block'; } ok = false; firstBad = firstBad || block; return; }
        }
        if (cat === 'Fit Issues') {
          var fp = (block.querySelector('[data-issue-fitpart]') || {}).value || '';
          if (!fp) { if (err) { err.textContent = 'Please select which part has the issue.'; err.style.display = 'block'; } ok = false; firstBad = firstBad || block; return; }
        }
        var desc = (block.querySelector('[data-issue-description]') || {}).value || '';
        if (!desc.trim()) { if (err) { err.textContent = 'Please add details about the issue.'; err.style.display = 'block'; } ok = false; firstBad = firstBad || block; return; }
        if (block.dataset.requiresPhoto === '1') {
          var photos = form._poState.photosByProduct[block.dataset.issueBlock] || [];
          if (photos.length === 0) { if (err) { err.textContent = 'A photo is required for this issue.'; err.style.display = 'block'; } ok = false; firstBad = firstBad || block; }
        }
      });
      if (!ok) { showError(form, 'Please complete the highlighted issue details.'); if (firstBad) firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      return ok;
    }
    return true;
  }

  /* ---------------- Stage navigation ---------------- */
  function goToStage(n, form, stages, steps, connectors) {
    form._poState.currentStage = n;
    stages.forEach(function (s) { s.classList.toggle('is-active', parseInt(s.dataset.stage, 10) === n); });
    steps.forEach(function (step) {
      var num = parseInt(step.dataset.progressStep, 10);
      step.classList.remove('is-active', 'is-completed', 'is-disabled');
      if (num < n) step.classList.add('is-completed');
      else if (num === n) step.classList.add('is-active');
      else step.classList.add('is-disabled');
    });
    connectors.forEach(function (c) { c.classList.toggle('is-completed', parseInt(c.dataset.progressConnector, 10) < n); });
    scheduleSave(form);
    var container = form.closest('.impression-form-v2');
    if (container) container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ---------------- Modals / banner / loading / tooltips ---------------- */
  function showThankYou(modal, ref) {
    if (!modal) return;
    var num = modal.querySelector('[data-confirmation-number]');
    if (num) num.textContent = ref || '---';
    modal.classList.add('is-open');
  }
  function showError(form, msg) {
    var banner = form.querySelector('.impression-form-v2__error-banner');
    if (banner) { banner.textContent = msg; banner.style.display = 'block'; banner.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  }
  function clearError(form) {
    var banner = form.querySelector('.impression-form-v2__error-banner');
    if (banner) banner.style.display = 'none';
  }
  function setLoading(form, on) {
    var txt = form.querySelector('.impression-form-v2__submit-text');
    var spin = form.querySelector('.impression-form-v2__loading');
    var btn = form.querySelector('button[type="submit"]');
    if (txt) txt.classList.toggle('is-hidden', on);
    if (spin) spin.classList.toggle('is-visible', on);
    if (btn) btn.disabled = on;
  }
  function initTooltips(root) {
    root.querySelectorAll('[data-tooltip-trigger]').forEach(function (trig) {
      var tip = trig.nextElementSibling;
      if (!tip) return;
      trig.addEventListener('click', function (e) { e.preventDefault(); tip.classList.toggle('is-visible'); });
    });
  }

  /* ---------------- Image helpers ---------------- */
  function compressImage(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          var w = img.width, h = img.height;
          if (w > MAX_IMAGE_DIM || h > MAX_IMAGE_DIM) {
            if (w > h) { h = Math.round(h * MAX_IMAGE_DIM / w); w = MAX_IMAGE_DIM; }
            else { w = Math.round(w * MAX_IMAGE_DIM / h); h = MAX_IMAGE_DIM; }
          }
          var canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          try { resolve(canvas.toDataURL('image/jpeg', IMAGE_QUALITY).split(',')[1]); }
          catch (err) { reject(err); }
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /* ---------------- Escaping ---------------- */
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }
})();
